<template>
  <div class="bui-dashboard-page kb-article-page">
    <!-- Top bar: back link + print -->
    <div class="kba-top-bar">
      <RouterLink to="/knowledge-base" class="kba-back-link">
        <PhArrowLeft :size="16" weight="bold" /> Knowledge Base
      </RouterLink>
      <button class="kba-print-button" @click="handlePrint">
        <PhPrinter :size="16" weight="bold" /> Print
      </button>
    </div>

    <ContentWithLoadingSkeleton :isLoading="isLoading">
      <div v-if="article" class="article-layout">
        <article class="kba-card article-content">
          <h1 class="article-title">{{ article.title }}</h1>
          <div class="article-meta">
            Modified on: {{ formatDate(article.updated_at || article.modified_at) }}
          </div>
          <div class="article-description" v-html="article.description"></div>
        </article>

        <aside v-if="relatedArticles.length > 0" class="related-articles-sidebar">
          <div class="kba-card related-articles-sticky">
            <h2 class="related-articles-title">Related Articles</h2>
            <ul class="related-articles-list">
              <li v-for="relatedArticle in relatedArticles" :key="relatedArticle.id">
                <PhFile :size="14" weight="regular" class="related-article-icon" />
                <RouterLink :to="`/knowledge-base/article/${relatedArticle.id}`" class="related-article-link">
                  {{ relatedArticle.name }}
                </RouterLink>
              </li>
            </ul>
          </div>
        </aside>
      </div>
      <div v-else-if="!isLoading" class="kba-error">
        Article not found
      </div>
    </ContentWithLoadingSkeleton>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { PhArrowLeft, PhPrinter, PhFile } from '@phosphor-icons/vue';
import ContentWithLoadingSkeleton from '@/components/ContentLoadingSkeleton/ContentWithLoadingSkeleton.vue';
import { fetchArticleById, fetchRelatedArticles } from '@/helpers/api/knowledgeBase/freshdeskKnowledgeBase';
import type { Article } from '@/helpers/api/knowledgeBase/types';

const route = useRoute();

const article = ref<Article | null>(null);
const relatedArticles = ref<Array<{ id: number; name: string }>>([]);
const isLoading = ref(false);

function formatDate(dateString: string | undefined): string {
  if (!dateString) return 'Unknown';

  try {
    const date = new Date(dateString);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const dayName = days[date.getUTCDay()];
    const month = months[date.getUTCMonth()];
    const day = date.getUTCDate();
    const year = date.getUTCFullYear();

    let hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;

    return `${dayName}, ${month} ${day}, ${year} at ${hours}:${minutesStr} ${ampm}`;
  } catch (error) {
    return dateString;
  }
}

async function loadArticle() {
  const articleId = route.params.id;
  if (!articleId || typeof articleId !== 'string') {
    return;
  }

  isLoading.value = true;
  try {
    const articleIdNum = parseInt(articleId, 10);
    article.value = await fetchArticleById(articleIdNum);

    // Fetch related articles
    try {
      relatedArticles.value = await fetchRelatedArticles(articleIdNum);
    } catch (error) {
      console.error('Failed to load related articles:', error);
      relatedArticles.value = [];
    }
  } catch (error) {
    console.error('Failed to load article:', error);
    article.value = null;
  } finally {
    isLoading.value = false;
  }
}

function handlePrint() {
  if (!article.value) return;

  const printWindow = window.open('', '', 'width=800,height=600');
  if (!printWindow) return;

  const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${article.value.title}</title>
      <style>
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
          line-height: 1.6;
          color: #25294A;
          max-width: 900px;
          margin: 20px auto;
          padding: 20px;
        }
        h1 {
          font-size: 2.5em;
          font-weight: bold;
          color: #25294A;
          margin-bottom: 1rem;
          letter-spacing: 1px;
        }
        .article-meta {
          color: #666;
          font-size: 0.9em;
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e6e6e6;
        }
        img {
          max-width: 100%;
          height: auto;
          margin: 1rem 0;
        }
        p {
          margin: 1rem 0;
        }
        ul, ol {
          margin: 1rem 0;
          padding-left: 2rem;
        }
        li {
          margin: 0.5rem 0;
        }
        h2, h3, h4, h5, h6 {
          margin: 1.5rem 0 1rem 0;
          color: #25294A;
          font-weight: bold;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 1rem 0;
        }
        th, td {
          border: 1px solid #e6e6e6;
          padding: 0.75rem;
          text-align: left;
        }
        th {
          background-color: #f5f5f5;
          font-weight: bold;
        }
        @media print {
          body {
            margin: 0;
            padding: 20px;
          }
          h1 {
            page-break-after: avoid;
          }
          img {
            page-break-inside: avoid;
          }
        }
      </style>
    </head>
    <body>
      <h1>${article.value.title}</h1>
      <div class="article-meta">
        Modified on: ${formatDate(article.value.updated_at || article.value.modified_at)}
      </div>
      <div class="article-content">
        ${article.value.description}
      </div>
    </body>
    </html>
  `;

  printWindow.document.write(printContent);
  printWindow.document.close();

  printWindow.onload = () => {
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };
}

onMounted(() => {
  loadArticle();
});

// Watch for route parameter changes to reload article when navigating between articles
watch(() => route.params.id, () => {
  loadArticle();
});
</script>

<style lang="scss" scoped>
// Page canvas — same surface as the redesigned Knowledge Base home.
.kb-article-page {
  background-color: var(--bui-page-fill, #ff007b);
  flex: 1 1 auto;
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
  justify-content: flex-start;
  padding: var(--bui-page-pad-y, 32px) var(--bui-page-pad-x, 48px);

  @include screen-width-down(768px) {
    padding: 24px 20px;
  }
}

.kba-top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: var(--bui-page-section-gap, 24px);
}

.kba-back-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--bui-page-head, #fffcfd);
  text-decoration: none;
  font-family: 'Robout', system-ui, sans-serif;
  font-size: 1rem;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--bui-page-head, #fffcfd);
    outline-offset: 2px;
    border-radius: 2px;
  }
}

.kba-print-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 var(--bui-ter-pad, 14px);
  height: 40px;
  background: var(--bui-ter-fill, rgba(246, 247, 255, 0.16));
  border: 1px solid var(--bui-ter-border, rgba(246, 247, 255, 0.04));
  border-radius: var(--bui-ter-radius, 8px);
  color: var(--bui-ter-text, #ffd9e6);
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: var(--bui-ter-fill-hover, rgba(246, 247, 255, 0.28));
  }

  &:focus-visible {
    outline: 2px solid var(--bui-page-head, #fffcfd);
    outline-offset: 2px;
  }
}

// Reading cards sit on the brand surface like InfoCards.
.kba-card {
  background: var(--bui-tbl-row-fill, #fffcfd);
  color: var(--bui-tbl-row-text, #2a2969);
  border-radius: var(--bui-tbl-card-radius, 12px);
  box-shadow: var(--bui-tbl-card-shadow, 0 8px 32px rgba(75, 24, 134, 0.16));
  padding: 1.5rem 1.75rem;
}

.article-layout {
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
}

.article-content {
  flex: 1;
  min-width: 0;
  max-width: 900px;
}

.article-title {
  font-family: 'Robout', system-ui, sans-serif;
  font-size: clamp(24px, 3vw, 34px);
  font-weight: 700;
  color: var(--bui-tbl-row-text, #2a2969);
  margin: 0 0 0.75rem;
}

.article-meta {
  color: var(--bui-tbl-detail-text, #5c5c66);
  font-size: 0.85em;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--bui-tbl-line, #ffd9e6);
}

.article-description {
  color: var(--bui-tbl-row-text, #2a2969);
  line-height: 1.6;
  font-size: 1rem;

  :deep(img) {
    max-width: 100%;
    height: auto;
    margin: 1rem 0;
  }

  :deep(p) {
    margin: 1rem 0;
  }

  :deep(a) {
    color: var(--bui-tbl-row-link, #4b1886);
  }

  :deep(ul),
  :deep(ol) {
    margin: 1rem 0;
    padding-left: 2rem;
  }

  :deep(li) {
    margin: 0.5rem 0;
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4),
  :deep(h5),
  :deep(h6) {
    margin: 1.5rem 0 1rem 0;
    color: var(--bui-tbl-row-text, #2a2969);
    font-weight: bold;
  }

  :deep(table) {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
  }

  :deep(th),
  :deep(td) {
    border: 1px solid var(--bui-tbl-line, #ffd9e6);
    padding: 0.75rem;
    text-align: left;
  }

  :deep(th) {
    background-color: var(--bui-ter-input-fill, rgba(0, 0, 0, 0.04));
    font-weight: bold;
  }
}

.kba-error {
  color: var(--bui-page-head, #fffcfd);
  font-size: 1.2rem;
  text-align: center;
  padding: 2rem;
}

.related-articles-sidebar {
  flex-shrink: 0;
  width: 300px;
}

.related-articles-sticky {
  position: sticky;
  top: 2rem;
}

.related-articles-title {
  font-family: 'Robout', system-ui, sans-serif;
  font-size: 1.05em;
  font-weight: 700;
  color: var(--bui-tbl-row-text, #2a2969);
  margin: 0 0 1rem;
}

.related-articles-list {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin: 0.75rem 0;
  }
}

.related-article-icon {
  flex-shrink: 0;
  margin-top: 3px;
  color: var(--bui-tbl-detail-text, #5c5c66);
}

.related-article-link {
  color: var(--bui-tbl-row-link, #4b1886);
  text-decoration: none;
  font-size: 0.95rem;
  line-height: 1.5;

  &:hover {
    text-decoration: underline;
  }
}

@media (max-width: 1024px) {
  .article-layout {
    flex-direction: column;
  }

  .related-articles-sidebar {
    width: 100%;
    max-width: 600px;
  }

  .related-articles-sticky {
    position: static;
  }
}

@media print {
  // Keep this for fallback, but the new method uses a separate window
  .kba-top-bar {
    display: none;
  }
}
</style>